import React, { useState, useEffect, useMemo, useCallback } from 'react';
import omit from 'lodash/omit';

import { withStyles } from '@material-ui/core/styles';
import MuiTable from '@material-ui/core/Table';
import MuiTableBody from '@material-ui/core/TableBody';
import MuiTableCell from '@material-ui/core/TableCell';
import MuiTableContainer from '@material-ui/core/TableContainer';
import MuiTableHead from '@material-ui/core/TableHead';
import MuiTableRow from '@material-ui/core/TableRow';
import MuiPaper from '@material-ui/core/Paper';
import MuiCircularProgress from '@material-ui/core/CircularProgress';
import AlertToast from 'components/AlertToast';
import { useGetDsps, useSegmentedPatchDsp } from '../../dataLayer/dsps';
import { areFieldsEqual } from 'utils/helpers';
import RowEditButtons from 'components/RowEditButtons';
import Row from './row';

const styles = () => ({
  loadWrapper: {
    display: 'flex',
    justifyContent: 'center',
    margin: '1rem 0',
  },
});

const createRowData = dspData => {
  const {
    id,
    identifier,
    name,
    sound_recording_support,
    video_support,
    system_admin_only,
    supports_ddex_timed_release,
  } = dspData;

  return {
    id: Number(id),
    identifier,
    name,
    sound_recording_support,
    video_support,
    system_admin_only,
    supports_ddex_timed_release,
  };
};

const TableDsps = props => {
  const { classes } = props;
  const [isEditingTable, setIsEditingTable] = useState(false);
  const [tableRows, setTableRows] = useState([]);
  const [editedRowMap, setEditedRowMap] = useState({});
  const [errorToastMessage, setErrorToastMessage] = useState('');
  const [submittingForm, setSubmittingForm] = useState(false);

  const getDspsQuery = useGetDsps();
  const dspPatchMutation = useSegmentedPatchDsp({
    onSuccess: () => {
      getDspsQuery.refetch();
    },
    onError: error => {
      setErrorToastMessage(error.toString());
    },
  });

  const setTableFromData = dspIdMap => {
    const tableRows = Object.values(dspIdMap);
    tableRows.sort((a, b) => a.id - b.id);
    setTableRows(tableRows);
  };

  const dspIdMap = useMemo(() => {
    const { data = { data: {} } } = getDspsQuery;

    const results = data?.data?.data?.results || [];

    const dspIdMap = results.reduce((acc, curr) => {
      acc[curr.id] = createRowData(curr);
      return acc;
    }, {});
    setTableFromData(dspIdMap);
    return dspIdMap;
  }, [getDspsQuery.data]);

  const handleFormSubmit = event => {
    try {
      event.preventDefault();
      setSubmittingForm(true);

      const dspsToPatch = Object.keys(editedRowMap)
        .map(editedId => editedRowMap[editedId])
        .filter(editedDspData => {
          const originalDspData = dspIdMap[editedDspData.id];
          const changedDDEXTimedRelease =
            originalDspData.supports_ddex_timed_release !== editedDspData.supports_ddex_timed_release;

          return changedDDEXTimedRelease
            ? confirm(
              `Adjusting Automated Timed Releases for ${editedDspData.identifier} will not change already queued releases today and may require adjustments for already submitted releases.`
            )
            : true;
        });

      if (dspsToPatch.length === 0) {
        setTableFromData(dspIdMap);
        return;
      }

      const mutationConfigs = dspsToPatch.map(dspData => ({ id: dspData.id, data: omit(dspData, 'id') }));
      dspPatchMutation.mutate({ mutationConfigs });
    } finally {
      setSubmittingForm(false);
      setIsEditingTable(false);
    }
  };

  const onRowInputChange = useCallback((event, value, index) => {
    const rowKeyToChange = event.target.name;
    //MUI Checkboxes and Textfields pass values back in different ways. Checkboxes use value, textboxes use target value.
    value = value != undefined ? value : event.target.value;

    setTableRows(currentTableRows => {
      //need a shallow copy so react knows to rerender.
      const tableRowsShallowCopy = [...currentTableRows];
      const rowData = tableRowsShallowCopy[index];
      const newRowData = { ...rowData, [rowKeyToChange]: value };
      const originalDspData = dspIdMap[rowData.id];
      tableRowsShallowCopy[index] = newRowData;

      setEditedRowMap(currentEditedRowMap => {
        const editedFromOriginal = !areFieldsEqual(
          ['name', 'sound_recording_support', 'video_support', 'system_admin_only', 'supports_ddex_timed_release'],
          originalDspData,
          newRowData
        );

        if (editedFromOriginal) {
          currentEditedRowMap[newRowData.id] = newRowData;
        } else {
          delete currentEditedRowMap[newRowData.id];
        }
        return currentEditedRowMap;
      });

      return tableRowsShallowCopy;
    });
  });

  const onEditSwitch = useCallback(isEditing => {
    if (isEditing) {
      setEditedRowMap({});
    } else {
      setTableFromData(dspIdMap);
    }
    setIsEditingTable(isEditing);
  });

  useEffect(() => {
    if (getDspsQuery.isError) {
      setErrorToastMessage(getDspsQuery.error.toString());
    }
  }, [getDspsQuery.isError]);

  return (
    <>
      <AlertToast message={errorToastMessage} />
      <MuiTableContainer component={MuiPaper}>
        <form onSubmit={handleFormSubmit}>
          {getDspsQuery.isFetching || dspPatchMutation.isLoading ? (
            <div className={classes.loadWrapper}>
              <MuiCircularProgress />
            </div>
          ) : (
            <MuiTable aria-label="collapsible table">
              <MuiTableHead>
                <MuiTableRow>
                  <MuiTableCell>id</MuiTableCell>
                  <MuiTableCell>Identifier</MuiTableCell>
                  <MuiTableCell>Name</MuiTableCell>
                  <MuiTableCell>Sound</MuiTableCell>
                  <MuiTableCell>Video</MuiTableCell>
                  <MuiTableCell>Sys. Admin Only</MuiTableCell>
                  <MuiTableCell>Automated Timed Releases</MuiTableCell>
                  <MuiTableCell align="right">
                    <RowEditButtons
                      disabled={submittingForm}
                      editing={isEditingTable}
                      onEditChange={onEditSwitch}
                      editIcon="table"
                    />
                  </MuiTableCell>
                </MuiTableRow>
              </MuiTableHead>
              <MuiTableBody>
                {tableRows.map((rowData, index) => (
                  <Row
                    key={rowData.id}
                    row={rowData}
                    editing={isEditingTable}
                    onInputChange={(event, value) => onRowInputChange(event, value, index)}></Row>
                ))}
              </MuiTableBody>
            </MuiTable>
          )}
        </form>
      </MuiTableContainer>
    </>
  );
};

TableDsps.defaultProps = {};

export default withStyles(styles)(TableDsps);
